import { useMemo } from 'react';
import type { MVRCarrierRule } from 'api';

interface UseMvrCarrierRulesParams {
  carrierId: number | null;
  carrierRules: MVRCarrierRule[];
}

interface PortalAdvisoryResult {
  showAdvisory: boolean;
  portalUrl: string | null;
}

export type PolicyNumberVerdict =
  | 'valid'
  | 'mismatch'
  | 'soft'
  | 'unknown'
  | null;

interface PolicyNumberVerdictResult {
  verdict: PolicyNumberVerdict;
  expectedPatternText: string | null;
}

const isRealUrl = (url: string | null | undefined): boolean =>
  !!url && /^https?:\/\//i.test(url.trim());

export const useMvrCarrierRules = ({
  carrierId,
  carrierRules,
}: UseMvrCarrierRulesParams) => {
  const rule: MVRCarrierRule | null =
    carrierRules.find((row) => row.carrier_id === carrierId) ?? null;

  const compiledPattern = useMemo(() => {
    const pattern = rule?.validation?.pattern_regex;
    if (!pattern) return null;
    try {
      return new RegExp(pattern, 'i');
    } catch {
      return null;
    }
  }, [rule?.validation?.pattern_regex]);

  const portalAdvisory: PortalAdvisoryResult =
    rule?.mvr_method === 'carrier_portal'
      ? {
          showAdvisory: true,
          portalUrl: isRealUrl(rule.portal_link) ? rule.portal_link : null,
        }
      : { showAdvisory: false, portalUrl: null };

  const isBlocked: boolean = rule?.mvr_method === 'carrier_underwriter';

  const getPolicyNumberVerdict = (
    policyNumber: string,
  ): PolicyNumberVerdictResult => {
    if (!policyNumber.trim() || !rule) {
      return { verdict: null, expectedPatternText: null };
    }
    const validation = rule.validation;
    if (!validation || !validation.confidence) {
      return {
        verdict: 'unknown',
        expectedPatternText: validation?.pattern_description ?? null,
      };
    }
    if (validation.confidence === 'soft') {
      return {
        verdict: 'soft',
        expectedPatternText: validation.pattern_description,
      };
    }
    if (!compiledPattern) {
      return {
        verdict: 'unknown',
        expectedPatternText: validation.pattern_description,
      };
    }
    return compiledPattern.test(policyNumber)
      ? {
          verdict: 'valid',
          expectedPatternText: validation.pattern_description,
        }
      : {
          verdict: 'mismatch',
          expectedPatternText: validation.pattern_description,
        };
  };

  return { portalAdvisory, isBlocked, getPolicyNumberVerdict };
};
